import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';

const CONSENT_KEY = 'prm_cookie_consent'; // 'accepted' | 'rejected'

const MARKETING_PATHS = [
  '/',
  '/passengers',
  '/business',
  '/about-us',
  '/contact',
  '/request-demo',
  '/download',
  '/privacy',
  '/website-privacy',
  '/terms',
  '/cookies',
];

const OPEN_SETTINGS_EVENT = 'prm:open-cookie-settings';

// Analytics/marketing cookies to clear when consent is withdrawn
const NON_ESSENTIAL_COOKIE_PREFIXES = ['_ga', '_gid', '_gat', '_gcl', '_fbp', '_fbc', 'li_', 'lidc', 'bcookie', 'AnalyticsSyncHistory', 'UserMatchHistory'];

// Re-open the banner so visitors can change their choice (footer "Cookie settings" link)
export const openCookieSettings = () => {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
};

const clearNonEssentialCookies = () => {
  const host = window.location.hostname;
  const domains = ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`];
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (!NON_ESSENTIAL_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
    });
  });
};

// Read consent state — returns 'accepted', 'rejected', or null
export const getConsent = () => {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
};

const styles = {
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#1B4F8A',
    color: '#ffffff',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
    zIndex: 9999,
    fontFamily: 'Inter, Arial, sans-serif',
    fontSize: '14px',
    lineHeight: '1.5',
    boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.15)',
  },
  text: {
    flex: '1 1 400px',
    margin: 0,
  },
  link: {
    color: '#D6E4F3',
    textDecoration: 'underline',
  },
  buttons: {
    display: 'flex',
    gap: '10px',
    flexShrink: 0,
  },
  accept: {
    backgroundColor: '#2E86C1',
    color: '#ffffff',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'Inter, Arial, sans-serif',
    fontSize: '14px',
    fontWeight: 600,
  },
  reject: {
    backgroundColor: 'transparent',
    color: '#D6E4F3',
    border: '1px solid #D6E4F3',
    padding: '10px 24px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: 'Inter, Arial, sans-serif',
    fontSize: '14px',
    fontWeight: 600,
  },
};

const CookieConsent = ({ onConsentChange }) => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent && MARKETING_PATHS.includes(pathname)) {
      setVisible(true);
    }
  }, [pathname]);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);

  const handleChoice = (choice) => {
    const previous = getConsent();
    try {
      localStorage.setItem(CONSENT_KEY, choice);
    } catch {
      // localStorage unavailable — consent still applies for this session
    }
    setVisible(false);
    if (onConsentChange) onConsentChange(choice);

    // GTM can't be unloaded once running, so withdrawing consent clears its cookies and reloads the page
    if (previous === 'accepted' && choice === 'rejected') {
      clearNonEssentialCookies();
      window.location.reload();
    }
  };

  if (!visible) return null;

  return (
    <div style={styles.banner} role="dialog" aria-label="Cookie consent">
      <p style={styles.text}>
        We use analytics and marketing cookies to understand how visitors use our website and to measure our advertising. We only set them if you accept.{' '}
        <Link to="/cookies" style={styles.link}>Cookie Policy</Link>{' · '}
        <Link to="/website-privacy" style={styles.link}>Privacy Policy</Link>
      </p>
      <div style={styles.buttons}>
        <button style={styles.reject} onClick={() => handleChoice('rejected')}>
          Reject
        </button>
        <button style={styles.accept} onClick={() => handleChoice('accepted')}>
          Accept
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
